import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adressenApi, type AdresseUpdateData } from '../api/adressen';
import { anfragenApi } from '../api/anfragen';
import VerfahrenStateBadge from '../components/VerfahrenStateBadge';
import AssignmentWidget from '../components/AssignmentWidget';
import AufgabenBlock from '../components/AufgabenBlock';
import AuditTimeline from '../components/AuditTimeline';
import DocumentList from '../components/DocumentList';
import KommunikationSection from '../components/KommunikationSection';
import BebauungsplanInfoCard from '../components/BebauungsplanInfoCard';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import MapLinkButton from '../components/MapLinkButton';
import { restoreOne } from '../api/softDelete';
import { useCan } from '../hooks/useCan';
import { useIsModuleEnabled, useDocumentConfig } from '../hooks/useConfigVisibility';

export default function AdresseDetail() {
  const { adresseId } = useParams<{ adresseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'dokumente' | 'verlauf'>('uebersicht');
  const docTab = useDocumentConfig('adresse').tab;
  const canRestore = useCan('restore_own');
  const showHaeuser = useIsModuleEnabled('haeuser');
  const showAnfragen = useIsModuleEnabled('anfragen');
  const showVerfahren = useIsModuleEnabled('verfahren');
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('adresse', Number(adresseId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adresse', adresseId] }),
  });

  const { data: adresse, isLoading, error } = useQuery({
    queryKey: ['adresse', adresseId],
    queryFn: () => adressenApi.getAdresse(Number(adresseId)),
    enabled: !!adresseId,
  });

  const { data: verfahrenList } = useQuery({
    queryKey: ['adresse', adresseId, 'verfahren'],
    queryFn: () => adressenApi.getVerfahrenAtAdresse(Number(adresseId)),
    enabled: !!adresseId,
  });

  const { data: anfragenList } = useQuery({
    queryKey: ['adresse', adresseId, 'anfragen'],
    queryFn: () => anfragenApi.getAnfragenAtAdresse(Number(adresseId)),
    enabled: !!adresseId,
  });

  const { data: inserateList } = useQuery({
    queryKey: ['adresse', adresseId, 'inserate'],
    queryFn: () => adressenApi.getInserateAtAdresse(Number(adresseId)),
    enabled: !!adresseId,
  });

  const { data: history } = useQuery({
    queryKey: ['adresse', adresseId, 'verlauf'],
    queryFn: () => adressenApi.getHistory(Number(adresseId)),
    enabled: !!adresseId && activeTab === 'verlauf',
  });

  const updateMutation = useMutation({
    mutationFn: (updates: AdresseUpdateData) =>
      adressenApi.updateAdresse(Number(adresseId), updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adresse', adresseId] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !adresse) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Adresse konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/adressen')} className="mt-4 text-blue-600 hover:text-blue-800">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/adressen')} className="text-blue-600 hover:text-blue-800 text-sm mb-2">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {adresse.strasse} {adresse.hausnummer}
              </h1>
              {(adresse.plz || adresse.stadt) && (
                <p className="text-sm text-gray-500 mt-1">
                  {[adresse.plz, adresse.stadt].filter(Boolean).join(' ')}
                </p>
              )}
              {adresse.latitude != null && adresse.longitude != null && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <MapLinkButton to={`/karte?adresse=${adresse.id}`} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {adresse.deleted_at ? (
                <>
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                    Ausgeblendet
                  </span>
                  {canRestore && (
                    <button
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="adresse"
        ids={[Number(adresseId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['adressen'] });
          navigate('/adressen');
        }}
      />

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'uebersicht', label: 'Übersicht' },
              ...(docTab ? [{ key: 'dokumente', label: 'Dokumente' }] : []),
              { key: 'verlauf', label: 'Verlauf' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {activeTab === 'uebersicht' && (
          <>
        <AssignmentWidget
          entityType="adresse"
          entityId={adresse.id}
          assignment={adresse.assignment ?? null}
          invalidateKeys={[['adresse', adresseId!], ['adressen']]}
        />
        <AufgabenBlock
          entityType="adresse"
          entityId={adresse.id}
          invalidateKeys={[['adresse', adresseId!]]}
        />

        {/* Adressdaten */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Adressdaten</h3>
          </div>
          <div className="px-6">
            <dl className="divide-y divide-gray-200">
              <InlineField label="Straße" value={adresse.strasse} onSave={(v) => updateMutation.mutate({ strasse: v })} />
              <InlineField label="Hausnummer" value={adresse.hausnummer} onSave={(v) => updateMutation.mutate({ hausnummer: v })} />
              <InlineField label="PLZ" value={adresse.plz || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ plz: v })} />
              <InlineField label="Stadt" value={adresse.stadt || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ stadt: v })} />
              <InlineField label="Notizen" value={adresse.notes || ''} placeholder="Keine Notizen" onSave={(v) => updateMutation.mutate({ notes: v })} multiline />
            </dl>
          </div>
        </div>

        {/* Bebauungsplan */}
        <BebauungsplanInfoCard latitude={adresse.latitude} longitude={adresse.longitude} />

        {/* Häuser */}
        {showHaeuser && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-medium">Häuser ({adresse.haeuser?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bezeichnung</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Baugenehmigung</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Objekte</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {adresse.haeuser?.map((h) => (
                <tr
                  key={h.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/haeuser/${h.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {h.typ && <span className="text-xs text-gray-500 mr-1 font-normal">{h.typ}</span>}
                    {h.bezeichnung || <span className="italic text-gray-400">Ohne Bezeichnung</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{h.baugenehmigung || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{h.einheiten_count}</td>
                </tr>
              ))}
              {(!adresse.haeuser || adresse.haeuser.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Häuser zugeordnet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Inserate */}
        {(
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Inserate ({inserateList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plattform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Online</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inserateList?.map((i) => (
                <tr
                  key={i.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/inserat/${i.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600 max-w-xs truncate">{i.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 capitalize">{i.platform}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                      {i.state}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {i.is_online ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Online</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">Offline</span>
                    )}
                  </td>
                </tr>
              ))}
              {(!inserateList || inserateList.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Keine Inserate an dieser Adresse.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Anfragen at this address */}
        {showAnfragen && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Anfragen ({anfragenList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {anfragenList?.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/anfragen/${a.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {a.adresse_text || `#${a.id}`}
                    {a.zipcode && <span className="text-gray-500 font-normal ml-1">{a.zipcode}</span>}
                    {a.city && <span className="text-gray-500 font-normal ml-1">{a.city}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.notes || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))}
              {(!anfragenList || anfragenList.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Anfragen an dieser Adresse.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Verfahren at this address */}
        {showVerfahren && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Verfahren ({verfahrenList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Einheit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Haus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {verfahrenList?.map((v) => (
                <tr
                  key={v.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/verfahren/${v.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {v.aktenzeichen || `#${v.id}`}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <VerfahrenStateBadge staat={v.staat} small />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.einheit_bezeichnung || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.haus_bezeichnung || '-'}</td>
                </tr>
              ))}
              {(!verfahrenList || verfahrenList.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Keine Verfahren an dieser Adresse.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
          </>
        )}

        {activeTab === 'dokumente' && (
          <div className="space-y-6">
            <DocumentList entityType="adresse" entityId={adresse.id} />
            <KommunikationSection entityType="adresse" entityId={adresse.id} />
          </div>
        )}

        {activeTab === 'verlauf' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium mb-4">Änderungsverlauf</h3>
            <AuditTimeline entries={history || []} />
          </div>
        )}
      </main>
    </div>
  );
}

function InlineField({ label, value, placeholder, onSave, multiline }: {
  label: string;
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
  multiline?: boolean;
}) {
  const [localValue, setLocalValue] = useState(value);
  const [dirty, setDirty] = useState(false);

  const handleChange = (val: string) => {
    setLocalValue(val);
    setDirty(val !== value);
  };

  const handleBlur = () => {
    if (dirty) {
      onSave(localValue);
      setDirty(false);
    }
  };

  const inputClass = "w-full bg-transparent border border-transparent rounded px-2 py-1 text-sm text-gray-900 hover:border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors";

  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 sm:mt-0 sm:col-span-2">
        {multiline ? (
          <textarea
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholder}
            rows={2}
            className={inputClass + " resize-none"}
          />
        ) : (
          <input
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={inputClass}
          />
        )}
      </dd>
    </div>
  );
}
